import { useEffect, useMemo, useState } from 'react';
import { Search, UserPlus, User } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/lib/api';

const fullName = (c) => `${c.first_name || ''} ${c.last_name || ''}`.trim();

export const customerToRecipient = (c) => ({
  customer_id: c.id,
  customer_number: c.customer_number || '',
  recipient_name: fullName(c),
  recipient_contact: '',
  recipient_street: c.street || '',
  recipient_city: `${c.postal_code || ''} ${c.city || ''}`.trim(),
  recipient_email: c.email || '',
});

export const CustomerPickerDialog = ({ open, onOpenChange, onPick }) => {
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (open && customers.length === 0) {
      api.get('/customers').then(({ data }) => setCustomers(Array.isArray(data) ? data : data.customers || [])).catch(() => {});
    }
  }, [open, customers.length]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return customers.filter((c) => !q || [fullName(c), c.email, c.city, c.customer_number].some((v) => (v || '').toLowerCase().includes(q))).slice(0, 50);
  }, [customers, query]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="letter-customer-picker">
        <DialogHeader>
          <DialogTitle>An wen geht der Brief?</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Kunde suchen (Name, Ort, Kundennr.)" className="pl-9" data-testid="letter-customer-search" />
        </div>
        <div className="max-h-[45vh] overflow-y-auto -mx-2">
          {filtered.map((c) => (
            <button key={c.id} type="button" onClick={() => onPick(customerToRecipient(c))} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left hover:bg-[#9B1B30]/5 transition-colors" data-testid={`letter-customer-${c.id}`}>
              <User className="w-4 h-4 text-[#9B1B30] shrink-0" />
              <span className="min-w-0">
                <span className="block text-sm font-medium truncate">{fullName(c) || c.email}</span>
                <span className="block text-xs text-gray-500 truncate">{[c.street, `${c.postal_code || ''} ${c.city || ''}`.trim(), c.customer_number].filter(Boolean).join(' · ')}</span>
              </span>
            </button>
          ))}
          {filtered.length === 0 && <p className="text-sm text-gray-500 px-3 py-6 text-center">Keine Kunden gefunden</p>}
        </div>
        <Button variant="outline" onClick={() => onPick({ customer_id: null, customer_number: '', recipient_name: '', recipient_contact: '', recipient_street: '', recipient_city: '', recipient_email: '' })} data-testid="letter-free-recipient-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Empfänger frei eingeben
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerPickerDialog;
